import { z } from 'zod'

import type { EmojiSearchData } from '~publish/legacy/editor/plugins/emoji/types'

const EmojibaseSkinSchema = z.object({
  emoji: z.string(),
  hexcode: z.string().optional(),
})

const EmojibaseEmojiSchema = z.object({
  label: z.string(),
  emoji: z.string(),
  hexcode: z.string(),
  tags: z.array(z.string()).optional(),
  version: z.number(),
  skins: z.array(EmojibaseSkinSchema).optional(),
})

const EmojibaseDataSchema = z.array(EmojibaseEmojiSchema)
const EmojibaseShortcodesSchema = z.record(
  z.string(),
  z.union([z.string(), z.array(z.string())]),
)

type EmojibaseSkin = {
  emoji: string
  hexcode?: string
}

type EmojibaseEmoji = {
  label: string
  emoji: string
  hexcode: string
  tags?: string[]
  version: number
  skins?: EmojibaseSkin[]
}

type EmojibaseShortcodes = z.infer<typeof EmojibaseShortcodesSchema>

const EMOJIBASE_BASE_URL =
  'https://cdn.jsdelivr.net/npm/emojibase-data@17.0.0/en'
const EMOJIBASE_DATA_URL = `${EMOJIBASE_BASE_URL}/data.json`
const EMOJIBASE_SHORTCODES_URL = `${EMOJIBASE_BASE_URL}/shortcodes/emojibase.json`
const MAX_SEARCH_RESULTS = 20
const EMOJIBASE_FETCH_TIMEOUT_MS = 10_000

let emojiDataPromise: Promise<EmojiSearchData[]> | null = null

const normalizeSearchTerm = (term: string): string =>
  term
    .trim()
    .replace(/^:+|:+$/g, '')
    .toLowerCase()

const shortcodeValueToList = (
  shortcodeValue: EmojibaseShortcodes[string] | undefined,
): string[] => {
  if (!shortcodeValue) return []

  return Array.isArray(shortcodeValue) ? shortcodeValue : [shortcodeValue]
}

const emojibaseEmojiToSearchData = (
  emoji: EmojibaseEmoji,
  shortcodesByHexcode: EmojibaseShortcodes,
): EmojiSearchData | null => {
  const shortcodes = shortcodeValueToList(shortcodesByHexcode[emoji.hexcode])
  const id = shortcodes[0]
  if (!id) return null

  return {
    id,
    name: emoji.label,
    keywords: emoji.tags ?? [],
    skins: [
      {
        unified: emoji.hexcode,
        native: emoji.emoji,
        shortcodes: id,
      },
      ...(emoji.skins ?? []).map((skin) => ({
        unified: skin.hexcode ?? emoji.hexcode,
        native: skin.emoji,
        shortcodes:
          shortcodeValueToList(shortcodesByHexcode[skin.hexcode ?? ''])[0] ??
          id,
      })),
    ],
    version: emoji.version,
    aliases: shortcodes.slice(1),
    search: [emoji.label, ...shortcodes, ...(emoji.tags ?? [])]
      .join(' ')
      .toLowerCase(),
  }
}

const fetchJson = async (url: string, label: string): Promise<unknown> => {
  const controller = new AbortController()
  const timeoutId = window.setTimeout(
    () => controller.abort(),
    EMOJIBASE_FETCH_TIMEOUT_MS,
  )

  try {
    const response = await fetch(url, { signal: controller.signal })
    if (!response.ok) {
      throw new Error(`Failed to load ${label}: ${response.status}`)
    }

    return response.json()
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error(`Timed out loading ${label}`, { cause: error })
    }

    throw error
  } finally {
    window.clearTimeout(timeoutId)
  }
}

const loadEmojiData = async (): Promise<EmojiSearchData[]> => {
  if (!emojiDataPromise) {
    emojiDataPromise = Promise.all([
      fetchJson(EMOJIBASE_DATA_URL, 'Emojibase data'),
      fetchJson(EMOJIBASE_SHORTCODES_URL, 'Emojibase shortcodes'),
    ])
      .then(([emojis, shortcodesByHexcode]) => {
        const parsedShortcodes =
          EmojibaseShortcodesSchema.parse(shortcodesByHexcode)

        return EmojibaseDataSchema.parse(emojis)
          .map((emoji) => emojibaseEmojiToSearchData(emoji, parsedShortcodes))
          .filter((emoji): emoji is EmojiSearchData => emoji !== null)
      })
      .catch((error) => {
        emojiDataPromise = null
        throw error
      })
  }

  return emojiDataPromise
}

const getEmojiScore = (emoji: EmojiSearchData, query: string): number => {
  if (emoji.id === query) return 100
  if (emoji.aliases?.includes(query)) return 95
  if (emoji.id.startsWith(query)) return 80
  if (emoji.aliases?.some((alias) => alias.startsWith(query))) return 75
  if (emoji.name.toLowerCase().includes(query)) return 50
  if (emoji.keywords.some((keyword) => keyword.toLowerCase().includes(query))) {
    return 25
  }
  if (emoji.search?.includes(query)) return 10

  return 0
}

export const searchEmojis = async (
  searchTerm: string,
): Promise<EmojiSearchData[]> => {
  const query = normalizeSearchTerm(searchTerm)
  if (!query) return []

  const emojis = await loadEmojiData()

  return emojis
    .map((emoji) => ({ emoji, score: getEmojiScore(emoji, query) }))
    .filter(({ score }) => score > 0)
    .toSorted(
      (a, b) => b.score - a.score || a.emoji.id.localeCompare(b.emoji.id),
    )
    .slice(0, MAX_SEARCH_RESULTS)
    .map(({ emoji }) => emoji)
}

type EmojiShortcodeMatch = {
  emoji: EmojiSearchData
  skin?: EmojiSearchData['skins'][number]
}

const findEmojiShortcodeMatch = async (
  shortcode: string,
): Promise<EmojiShortcodeMatch | null> => {
  const query = normalizeSearchTerm(shortcode)
  if (!query) return null

  const emojis = await loadEmojiData()

  for (const emoji of emojis) {
    if (emoji.id === query || emoji.aliases?.includes(query)) {
      return { emoji }
    }

    const skin = emoji.skins.find((emojiSkin) => emojiSkin.shortcodes === query)
    if (skin) return { emoji, skin }
  }

  return null
}

export const findEmojiByShortcode = async (
  shortcode: string,
): Promise<EmojiSearchData | null> => {
  const match = await findEmojiShortcodeMatch(shortcode)
  return match?.emoji ?? null
}

export const emojiIdToNative = async (
  emojiId: string,
): Promise<string | null> => {
  const match = await findEmojiShortcodeMatch(emojiId)
  return match?.skin?.native ?? match?.emoji.skins[0]?.native ?? null
}

export const resetEmojiSearchCacheForTests = (): void => {
  emojiDataPromise = null
}
